import React, { useEffect, useMemo, useState } from 'react';
import type { WbsImportPreview } from '../lib/excelImport';
import { parseWbsImportFile } from '../lib/excelImport';

export default function WbsImportModal({
    open,
    busy,
    onClose,
    onConfirm,
}: {
    open: boolean;
    busy?: boolean;
    onClose: () => void;
    onConfirm: (preview: WbsImportPreview) => Promise<void> | void;
}) {
    const [fileName, setFileName] = useState('');
    // const [replaceDrafts, setReplaceDrafts] = useState(true);
    const [preview, setPreview] = useState<WbsImportPreview | null>(null);
    const [parsing, setParsing] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [showIssues, setShowIssues] = useState(true);

    const [validateWeight, setValidateWeight] = useState(true);
    const [validateDates, setValidateDates] = useState(true);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const errorCount = useMemo(() => preview?.issues.filter((x) => x.level === 'ERROR').length ?? 0, [preview]);
    const warningCount = useMemo(() => preview?.issues.filter((x) => x.level === 'WARNING').length ?? 0, [preview]);

    const parseSelectedFile = async (file: File) => {
        setParsing(true);
        setErrorText('');
        setPreview(null);
        setFileName(file.name);

        try {
            const next = await parseWbsImportFile(file, {
                validateWeight,
                validateDates,
            });
            setPreview(next);
        } catch (err: any) {
            console.error(err);
            setErrorText(err?.message ?? '파일 파싱 중 오류가 발생했습니다.');
        } finally {
            setParsing(false);
        }
    };

    useEffect(() => {
        if (!open) {
            setFileName('');
            // setReplaceDrafts(true);
            setPreview(null);
            setParsing(false);
            setErrorText('');
            setShowIssues(true);
            setValidateWeight(true);
            setValidateDates(true);
            setSelectedFile(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (!selectedFile) return;
        parseSelectedFile(selectedFile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedFile]);

    if (!open) return null;

    const handleFile = async (file?: File | null) => {
        if (!file) return;
        setSelectedFile(file);
        await parseSelectedFile(file);
    };

    const issuesHeaderTone = errorCount > 0 ? 'bg-rose-50' : warningCount > 0 ? 'bg-amber-50' : 'bg-gray-50';

    return (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4">
            <div className="w-full max-w-7xl max-h-[92vh] bg-white rounded-2xl border shadow-xl overflow-hidden flex flex-col">
                <div className="px-5 py-4 border-b flex items-center justify-between gap-4 shrink-0">
                    <div className="min-w-0">
                        <div className="text-lg font-semibold">WBS Excel Import</div>
                        <div className="text-sm text-gray-500 break-words">
                            Code / Wbs Name / Weight / Start / Finish 형식의 Excel 파일을 업로드합니다.
                        </div>
                    </div>

                    <button className="shrink-0 px-3 py-1 rounded border" onClick={onClose} type="button">
                        닫기
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto min-h-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                        />
                        {fileName && (
                            <span className="text-sm text-gray-500 max-w-[360px] truncate" title={fileName}>
                                {fileName}
                            </span>
                        )}
                    </div>
                    
                    <div className="flex items-center gap-4 flex-wrap text-sm">
                        <div className="font-medium text-gray-700">검증 항목 선택</div>

                        <label className="inline-flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={validateWeight}
                                onChange={(e) => setValidateWeight(e.target.checked)}
                            />
                            <span>가중치 검증</span>
                        </label>

                        <label className="inline-flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={validateDates}
                                onChange={(e) => setValidateDates(e.target.checked)}
                            />
                            <span>날짜 검증</span>
                        </label>
                    </div>

                    {parsing && <div className="text-sm text-gray-500">파일을 분석하고 있습니다...</div>}

                    {errorText && <div className="text-sm text-rose-600">{errorText}</div>}

                    {preview && (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <StatBox label="행 수" value={preview.rows.length} />
                                <StatBox label="생성 대상" value={preview.items.length} />
                                <StatBox label="오류" value={errorCount} danger={errorCount > 0} />
                                <StatBox label="경고" value={warningCount} />
                            </div>

                            <div className="space-y-4">
                                <section className="border rounded-xl overflow-hidden min-w-0">
                                    <button
                                        className={`w-full px-4 py-3 border-b flex items-center justify-between gap-3 text-left ${issuesHeaderTone}`}
                                        onClick={() => setShowIssues((v) => !v)}
                                        type="button"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-medium">검증 결과</div>
                                            <div className="text-xs text-gray-600 mt-0.5">
                                                Error {errorCount} / Warning {warningCount}
                                            </div>
                                        </div>

                                        <div className="shrink-0 text-sm text-gray-500">
                                            {showIssues ? '접기 ▲' : '펼치기 ▼'}
                                        </div>
                                    </button>

                                    {showIssues && (
                                        <div className="max-h-[240px] overflow-auto">
                                            {preview.issues.length === 0 ? (
                                                <div className="p-4 text-sm text-green-700">검증 오류가 없습니다.</div>
                                            ) : (
                                                <table className="w-full table-fixed text-sm">
                                                    <colgroup>
                                                        <col className="w-[90px]" />
                                                        <col className="w-[70px]" />
                                                        <col className="w-[90px]" />
                                                        <col className="w-[100px]" />
                                                        <col />
                                                    </colgroup>

                                                    <thead className="sticky top-0 z-10 bg-gray-50 border-b">
                                                        <tr className="text-left text-gray-700">
                                                            <th className="px-3 py-2 font-medium">Level</th>
                                                            <th className="px-3 py-2 font-medium">Row</th>
                                                            <th className="px-3 py-2 font-medium">Code</th>
                                                            <th className="px-3 py-2 font-medium">Field</th>
                                                            <th className="px-3 py-2 font-medium">Message</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {preview.issues.map((issue, idx) => (
                                                            <tr
                                                                key={`${issue.level}-${issue.rowNo}-${idx}`}
                                                                className="border-b last:border-b-0 hover:bg-gray-50"
                                                            >
                                                                <td className="px-3 py-2 align-top whitespace-nowrap">
                                                                    <span
                                                                        className={
                                                                            issue.level === 'ERROR'
                                                                                ? 'inline-flex rounded-full bg-rose-50 text-rose-700 px-2 py-0.5 text-xs font-semibold'
                                                                                : 'inline-flex rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-semibold'
                                                                        }
                                                                    >
                                                                        {issue.level}
                                                                    </span>
                                                                </td>

                                                                <td className="px-3 py-2 align-top whitespace-nowrap tabular-nums text-gray-700">
                                                                    {issue.rowNo ?? ''}
                                                                </td>

                                                                <td className="px-3 py-2 align-top whitespace-nowrap font-mono text-[13px] text-gray-700">
                                                                    {issue.code ?? ''}
                                                                </td>

                                                                <td className="px-3 py-2 align-top whitespace-nowrap text-gray-700">
                                                                    {issue.field ?? ''}
                                                                </td>

                                                                <td className="px-3 py-2 align-top break-words text-gray-900">
                                                                    {issue.message}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}
                                </section>

                                <section className="border rounded-xl overflow-hidden min-w-0">
                                    <div className="px-4 py-2 border-b bg-gray-50 flex items-center justify-between gap-3">
                                        <div className="font-medium">미리보기</div>
                                        <div className="text-xs text-gray-500">긴 WBS Name은 2줄까지 표시됩니다.</div>
                                    </div>

                                    <div className="max-h-[420px] overflow-auto">
                                        <table className="w-full table-fixed text-sm">
                                            <colgroup>
                                                <col className="w-[90px]" />
                                                <col className="w-[38%]" />
                                                <col className="w-[90px]" />
                                                <col className="w-[120px]" />
                                                <col className="w-[120px]" />
                                                <col className="w-[80px]" />
                                                <col className="w-[80px]" />
                                            </colgroup>

                                            <thead className="sticky top-0 z-10 bg-gray-50 border-b">
                                                <tr className="text-left text-gray-700">
                                                    <th className="px-3 py-2 font-medium">Code</th>
                                                    <th className="px-3 py-2 font-medium">WBS Name</th>
                                                    <th className="px-3 py-2 font-medium text-right">Weight</th>
                                                    <th className="px-3 py-2 font-medium">Start</th>
                                                    <th className="px-3 py-2 font-medium">Finish</th>
                                                    <th className="px-3 py-2 font-medium text-right">Days</th>
                                                    <th className="px-3 py-2 font-medium text-right">Order</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {preview.rows.map((row) => {
                                                    const depth = (row.code?.split('.').length ?? 1) - 1;
                                                    return (
                                                        <tr
                                                            key={`${row.rowNo}-${row.code}`}
                                                            className="border-b last:border-b-0 hover:bg-orange-50/40"
                                                        >
                                                            <td className="px-3 py-2 align-top font-mono text-[13px] whitespace-nowrap text-gray-700">
                                                                {row.code}
                                                            </td>

                                                            <td className="px-3 py-2 align-top">
                                                                <div
                                                                    className="break-words leading-5 text-gray-900"
                                                                    style={{
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: 'vertical',
                                                                        overflow: 'hidden',
                                                                        paddingLeft: `${depth * 16}px`,
                                                                    }}
                                                                    title={row.name}
                                                                >
                                                                    {row.name}
                                                                </div>
                                                            </td>

                                                            <td className="px-3 py-2 align-top text-right tabular-nums whitespace-nowrap text-gray-800">
                                                                {row.weight ?? ''}
                                                            </td>

                                                            <td className="px-3 py-2 align-top font-mono tabular-nums whitespace-nowrap text-gray-700">
                                                                {row.plannedStart ?? ''}
                                                            </td>

                                                            <td className="px-3 py-2 align-top font-mono tabular-nums whitespace-nowrap text-gray-700">
                                                                {row.plannedFinish ?? ''}
                                                            </td>

                                                            <td className="px-3 py-2 align-top text-right tabular-nums whitespace-nowrap text-gray-800">
                                                                {row.days ?? ''}
                                                            </td>

                                                            <td className="px-3 py-2 align-top text-right tabular-nums whitespace-nowrap text-gray-500">
                                                                {(Number(row.code.split('.').pop() ?? '1') || 1) * 10}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            </div>
                        </>
                    )}
                </div>

                <div className="px-5 py-4 border-t flex items-center justify-between gap-4 shrink-0">
                    <div className="text-sm text-gray-500 break-words">
                        Import 시 orderIndex는 Code 마지막 segment x 10 규칙으로 저장됩니다. 예: 1.2.3 → 30
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        <button className="px-4 py-2 rounded-xl border" onClick={onClose} type="button">
                            취소
                        </button>

                        <button
                            className="px-4 py-2 rounded-xl bg-orange-500 text-white disabled:opacity-50"
                            onClick={() => preview && onConfirm(preview)}
                            disabled={!preview || preview.items.length === 0 || errorCount > 0 || !!busy || parsing}
                            type="button"
                        >
                            {busy ? 'Import 중...' : 'Import 실행'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatBox({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
    return (
        <div className="border rounded-xl p-3">
            <div className="text-xs text-gray-500">{label}</div>
            <div className={danger ? 'text-lg font-semibold text-rose-600' : 'text-lg font-semibold'}>{value}</div>
        </div>
    );
}
